import React, { Component } from 'react';

import moment from 'moment';

import { DatePickerAPI, DatePickerMode, RangeState } from '@philips-emr/tws-components-api';
import { NativeDateType } from '@philips-emr/tws-constants';
import { AwesomeComponent, ComponentType } from '@philips-emr/tws-core';
import { array, arrayOf, bool, date, eventListener, oneOf, oneOfType, shape, string } from '@philips-emr/tws-core/lib/component/prop-types';
import { enumValues, getLostProps, isFunction, isMobile, noop, toMoment } from '@philips-emr/tws-utils';

import { NativeDate } from '..';
import { optimizeRanges } from '../calendar/calendar-util';

import DatePickerBasic from './basic/date-picker-basic';
import { decrementMode, getTypeOfNavigabilityMode, incrementMode } from './date-picker-util';
import DatePickerInterval from './interval/date-picker-interval';

import './style/date-picker.scss';

@AwesomeComponent({ type: ComponentType.DATA_ENTRY, api: DatePickerAPI })
class DatePicker extends Component {

  state = {
    date: this.props.initialDate,
    innerMode: this.props.mode,
    tempValue: [],
  };

  static propTypes = {
    data: array,
    externalControls: bool.notEnumerable(),
    initialDate: oneOfType([date, string]),
    interval: bool,
    loading: bool,
    mode: oneOf(enumValues(DatePickerMode)),
    nativeOnMobile: bool,
    onChange: eventListener,
    onNavigate: eventListener,
    ranges: arrayOf(shape({ startDate: string, endDate: string })).notEnumerable(),
    rangeState: oneOf(enumValues(RangeState)),
    showClear: bool,
    value: oneOfType([string, arrayOf(string)]),
  };

  static defaultProps = {
    data: [],
    initialDate: moment.utc().toISOString(),
    interval: false,
    loading: false,
    mode: DatePickerMode.MONTHLY,
    nativeOnMobile: false,
    ranges: [],
    rangeState: RangeState.DISABLED,
    showClear: true,
  };

  changeTempValue(tempValue) {
    const { externalControls } = this.props;

    this.setState({
      tempValue,
    }, () => {
      if (externalControls) {
        this.onApplyInterval();
      }
    });
  }

  onCellClick = (value) => {
    const { loading } = this.props;

    if (loading) {
      return;
    }

    const { innerMode } = this.state;
    const { mode } = this.props;

    this.setState(({ innerMode }) => ({
      innerMode: innerMode !== mode ? decrementMode(innerMode) : mode,
      date: moment(value).utc(),
    }));

    if (this.props.mode === innerMode) {
      this.onApply({ value });
    }
  };

  onIntervalCellClick = (selectedValue) => {
    const { loading } = this.props;

    if (loading) {
      return;
    }

    const [startDate, endDate] = this.state.tempValue;
    let tempValue;

    if (startDate && !endDate && selectedValue.isBefore(toMoment(startDate))) {
      tempValue = [selectedValue, toMoment(startDate)];
    } else if (!startDate || selectedValue.isBefore(toMoment(startDate))) {
      tempValue = [selectedValue, toMoment(endDate)];
    } else {
      tempValue = [toMoment(startDate), selectedValue];
    }

    this.changeTempValue(tempValue);
  };

  onTitleClick = () => {
    const { loading } = this.props;

    if (loading) {
      return;
    }

    this.setState(({ innerMode }) => ({
      innerMode: incrementMode(innerMode),
    }));
  };

  onClear = () => {
    const { interval, loading } = this.props;

    if (loading) {
      return;
    }

    if (interval) {
      this.changeTempValue([]);
    } else {
      this.setState({
        date: null,
      }, () => {
        this.onApply(null)
      });
    }
  };

  onToday = () => {
    const { externalControls, loading, onNavigate } = this.props;

    const { date } = this.state;

    if (loading) {
      return;
    }

    const { interval, mode } = this.props;
    const today = moment().utc();

    const currentDate = moment(date).utc();

    const dates = this.getDates(currentDate, today);

    isFunction(onNavigate) && onNavigate({ abort: noop, from: dates.initialDate, to: dates.finalDate });

    this.setState({
      date: today,
      innerMode: mode,
      tempValue: [today],
    }, () => {
      if (interval && externalControls) {
        this.onApplyInterval();
      }
    });

    if (!interval) this.onApply({ value: today });
  };

  onArrowClick = (value) => {
    const { loading, interval, onNavigate } = this.props;

    if (loading) {
      return;
    }

    this.setState(({ date, innerMode: mode }) => {
      const amount = (mode === DatePickerMode.DECADES ? value * 10 : value);
      const period = getTypeOfNavigabilityMode(mode);

      const currentDate = moment(date).utc();

      return { oldDate: date, date: currentDate.add(amount, period), amount, period };
    }, () => {
      const { amount, period } = this.state;
      const currentDate = moment(this.state.oldDate).utc();

      const nextDate = interval
        ? moment(currentDate).utc().add(amount * 2, period)
        : moment(currentDate).utc().add(amount, period);

      const dates = this.getDates(currentDate, nextDate, period);

      isFunction(onNavigate) && onNavigate({ abort: noop, from: dates.initialDate, to: dates.finalDate });
    });
  };

  getDates = (currentDate, nextDate, period) => {
    const initialCurrentDate = currentDate.startOf(period).toISOString();
    const finalCurrentDate = currentDate.endOf(period).toISOString();

    const nextInitialDate = nextDate.startOf(period).toISOString();
    const nextFinalDate = nextDate.endOf(period).toISOString();

    return {
      initialDate: [initialCurrentDate, finalCurrentDate],
      finalDate: [nextInitialDate, nextFinalDate],
    };
  };

  onApplyInterval = () => {
    const { loading } = this.props;

    if (loading) {
      return;
    }

    const { tempValue } = this.state;
    const { onChange, value: oldValue } = this.props;

    const value = tempValue.filter(Boolean).map(item => item.toISOString());

    isFunction(onChange) && onChange({ value, oldValue });
  };

  onApply = (date) => {
    const { onChange, value: oldValue } = this.props;

    const value = date && date.value.toISOString() || '';

    isFunction(onChange) && onChange({ value, oldValue });
  };

  getValue = () => {
    const { interval, value } = this.props;
    const { tempValue } = this.state;

    if (interval) {
      return tempValue;
    }

    return Array.isArray(value) ? toMoment(value[0]) : toMoment(value);
  };

  componentDidMount() {
    const { interval, value } = this.props;

    const date = Array.isArray(value) ? value[0] : value;
    const isValid = moment(date).isValid();

    if (date && isValid) {
      this.setState({ date: moment(date).utc() });
    }

    if (interval && value) {
      this.setState({ tempValue: [].concat(value).map(toMoment) });
    }
  }

  componentDidUpdate(prevProps) {
    const { interval, value, initialDate, mode } = this.props;
    const { value: prevValue, initialDate: prevInitialDate, mode: prevMode } = prevProps;

    if (initialDate !== prevInitialDate) {
      this.setState({ date: moment(initialDate).utc() });
    }

    if (mode !== prevMode) {
      this.setState({ innerMode: mode });
    }

    if(!interval) return;

    const [prevStartDate, prevEndDate] = prevValue || [];
    const [startDate, endDate] = value || [];

    if(prevStartDate !== startDate
      || prevEndDate !== endDate) {
      this.setState({
        tempValue: value.map(toMoment),
      });
    }

  }

  getNativeType() {
    const { mode } = this.props;

    const nativeTypes = {
      [DatePickerMode.MONTHLY]: NativeDateType.DATE,
      [DatePickerMode.YEARLY]: NativeDateType.MONTH,
    };

    return nativeTypes[mode] || NativeDateType.DATE;
  }

  renderMobile() {
    const type = this.getNativeType();

    return (
      <NativeDate type={type} value={this.getValue()} onChange={(e) => this.onCellClick(e.value)} />
    );
  }

  renderDatePicker() {
    const { data, externalControls, interval, loading, ranges, rangeState, showClear } = this.props;
    const { innerMode, date } = this.state;
    const value = this.getValue();

    const momentDate = toMoment(date);

    const lostProps = getLostProps(DatePicker.propTypes, this.props);

    const optimizedRanges = optimizeRanges(ranges);
    const rangeEnabled = rangeState === RangeState.ENABLED;

    return (
      interval
        ? <DatePickerInterval
            {...lostProps}
            date={momentDate}
            externalControls={externalControls}
            loading={loading}
            onArrowClick={this.onArrowClick}
            onCellClick={this.onIntervalCellClick}
            onClear={this.onClear}
            onClick={this.onApplyInterval}
            onToday={this.onToday}
            rangeEnabled={rangeEnabled}
            ranges={optimizedRanges}
            showClear={showClear}
            value={value}
          />
        : <DatePickerBasic
            {...lostProps}
            date={momentDate}
            events={data}
            externalControls={externalControls}
            loading={loading}
            mode={innerMode}
            onArrowClick={this.onArrowClick}
            onCellClick={this.onCellClick}
            onClear={this.onClear}
            onTitleClick={this.onTitleClick}
            onToday={this.onToday}
            rangeEnabled={rangeEnabled}
            ranges={optimizedRanges}
            showClear={showClear}
            showEvents={Boolean(data.length)}
            value={value}
          />
    );
  }

  render() {
    const { nativeOnMobile } = this.props;
    const isNative = isMobile() && nativeOnMobile;

    return isNative
      ? this.renderMobile()
      : this.renderDatePicker();
  }
}

export default DatePicker;
